import { ActionTypes, FormReducerAction, InitState } from "../types";

export const initState: InitState = {
    waitingOtp: false,
    submitted: false,
    loadingForm: false,
    currentStep: 0,
    fieldsState: {},
    formError: null,
    formErrors: 0,
    formErrorsResolved: false,
    stepStatus: "success",
    visitedSteps: {},
    otp: {
        isRequired: false,
        isResolved: false,
        countDown: 60,
    },
    showExitModal: false,
    blockBack: false,
    showRegulations: false,
    showCustomListingModal: false,
    showFormModal: false,
    showFromInExitModal: false,

    operation: null,
    globalLoading: true,
};

/**
 * Pure form reducer.
 *
 * NOTE: Side effects that previously lived inside `SET_FORM_SUBMITTED`
 * (clearing module-level `leadId` / `startSendingData`) are now handled
 * by the caller (see the dispatch wrapper in `useForm/index.tsx`).
 *
 * The reducer still performs two intentional browser side effects that
 * are kept for behavior parity:
 *  - `sessionStorage.removeItem` on form-submit
 *  - `window.history.pushState` on step transitions
 */
export const formReducer = (
    state: InitState,
    action: FormReducerAction,
): InitState => {
    switch (action.type) {
        case ActionTypes.SET_STEP:
            return {
                ...state,
                currentStep: action.payload,
                loadingForm: false,
            };
        case ActionTypes.SHOW_EXIT_MODAL:
            return {
                ...state,
                showExitModal: !!action.payload,
            };
        case ActionTypes.TOGGLE_SHOW_FORM_INSIDE_EXIT_MODAL:
            return {
                ...state,
                showFromInExitModal:
                    action.payload ?? !state.showFromInExitModal,
            };
        case ActionTypes.SET_FIELDS_STATE:
            return {
                ...state,
                fieldsState: { ...state.fieldsState, ...action.payload },
            };
        case ActionTypes.REPLACE_FIELDS_STATE:
            return {
                ...state,
                fieldsState: action.payload,
            };
        case ActionTypes.SET_LOADING_FORM:
            return {
                ...state,
                loadingForm: action.payload,
            };
        case ActionTypes.SET_FORM_SUBMITTED: {
            sessionStorage.removeItem(`form-${action.payload.formId}`);
            sessionStorage.removeItem(`lead-id-form-${action.payload.formId}`);
            const isResolved = action.payload.isOtpResolved ?? false;

            return {
                ...state,
                submitted: true,
                waitingOtp: false,
                formErrors: 0,
                otp: {
                    ...state.otp,
                    isRequired: false,
                    isResolved,
                },
            };
        }
        case ActionTypes.SET_STEP_STATUS:
            return {
                ...state,
                stepStatus: action.payload ? "success" : "error",
            };
        case ActionTypes.SET_FORM_ERROR_FIELD: {
            window.history.pushState(
                null,
                "",
                `#step-${state.currentStep + 2}`,
            );
            return {
                ...state,
                fieldsState: action.payload.fields,
                formErrors: action.payload.formErrors,
                formErrorsResolved: true,
                blockBack: true,
            };
        }
        case ActionTypes.RESET_FORM_SUBMIT:
            return {
                ...state,
                formErrors: 0,
                formErrorsResolved: false,
            };
        case ActionTypes.SET_FORM_ERROR:
            return {
                ...state,
                formError: action.payload,
            };
        case ActionTypes.SET_FORM_MODAL:
            return {
                ...state,
                showFormModal: action.payload,
            };
        case ActionTypes.SET_NEXT_STEP:
            window.history.pushState(
                null,
                "",
                `#step-${state.currentStep + 2}`,
            );
            return {
                ...state,
                currentStep: state.currentStep + 1,
                loadingForm: false,
                visitedSteps: {
                    ...state.visitedSteps,
                    [state.currentStep]: state.currentStep,
                },
            };
        case ActionTypes.SET_OTP:
            return {
                ...state,
                otp: action.payload,
                formErrors: 0,
            };
        case ActionTypes.SET_FORM_WAITING_OTP:
            return {
                ...state,
                waitingOtp: action.payload,
            };
        case ActionTypes.SHOW_REGULATIONS_PAGE:
            return {
                ...state,
                showRegulations: action.payload.showRegulations,
            };
        case ActionTypes.SHOW_CUSTOM_LISTING_MODAL:
            return {
                ...state,
                showCustomListingModal: action.payload.showCustomListingModal,
            };
        case ActionTypes.SET_OPERATION:
            return {
                ...state,
                operation: {
                    ...state.operation,
                    ...action.payload,
                },
                otp: {
                    ...state.otp,
                    isResolved:
                        action.payload.isOtpResolved ?? state.otp.isResolved,
                },
                formErrors: action.payload.formErrors ?? state.formErrors,
            };
        case ActionTypes.SET_GLOBAL_LOADING:
            return {
                ...state,
                globalLoading: action.payload,
            };
        default:
            return state;
    }
};
